import React, { useCallback, useEffect, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { useSubjectLanguageVariants } from '@features/user';
import { useAutoSave } from '@hooks/useAutoSave';
import { useEventTracking } from '@services/statsig';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useUpdatePatientModuleMutation } from '../store/api';
import type { PatientModuleInfo } from '../types';

import type { Diagnosis } from './PatientDiagnosesCard';
import { PatientDiagnosesEditCard } from './PatientDiagnosesEditCard';

interface PatientDiagnosesEditModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  diagnoses: Diagnosis[];
  moduleInfo: PatientModuleInfo;
  onDiagnosesUpdated?: () => void;
  /** Source identifier for analytics */
  source: string;
}

export const PatientDiagnosesEditModal: React.FC<PatientDiagnosesEditModalProps> = ({
  open,
  onClose,
  patientId,
  diagnoses: initialDiagnoses,
  moduleInfo,
  onDiagnosesUpdated,
  source,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { singularCap } = useSubjectLanguageVariants();
  const { patientContextBar: contextBarTracking } = useEventTracking();
  const [diagnoses, setDiagnoses] = useState<Diagnosis[]>(initialDiagnoses);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  const [updateModule, { isLoading }] = useUpdatePatientModuleMutation();

  // Reset diagnoses when modal opens/closes or initial diagnoses change
  useEffect(() => {
    if (open) {
      setDiagnoses(initialDiagnoses);
      setHasLocalChanges(false);
    }
  }, [open, initialDiagnoses]);

  const performSave = useCallback(
    async (diagsToSave: Diagnosis[]) => {
      try {
        // Transform diagnoses back to the format expected by the API
        const transformedData = diagsToSave.map(diag => ({
          name: diag.diagnosis_name,
          diagnosis_name: diag.diagnosis_name,
          code: diag.code,
          start_date: diag.start_date,
          end_date: diag.end_date,
          active: diag.isActive ?? true,
        }));

        await updateModule({
          patientId,
          patientModuleId: moduleInfo.patient_module_id,
          dataKey: moduleInfo.dataKey,
          data: transformedData,
        }).unwrap();
        setHasLocalChanges(false);
        onDiagnosesUpdated?.();
      } catch (error) {
        console.error('Failed to save diagnoses:', error);
        contextBarTracking.saveFailed({
          patient_id: patientId,
          data_type: 'diagnoses',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
        throw error;
      }
    },
    [patientId, moduleInfo, updateModule, onDiagnosesUpdated, contextBarTracking, source],
  );

  const { triggerAutoSave, saveImmediately, status, lastSavedAt } = useAutoSave({
    onSave: performSave,
    debounceMs: 3000,
    enabled: open && hasLocalChanges,
    flushOnUnmount: true, // Safety net for unexpected unmounts
  });

  const handleDiagnosesChange = useCallback(
    (newDiagnoses: Diagnosis[]) => {
      setDiagnoses(newDiagnoses);
      setHasLocalChanges(true);
      triggerAutoSave(newDiagnoses);
    },
    [triggerAutoSave],
  );

  // Handle modal close with flush of pending saves
  const handleClose = useCallback(async () => {
    contextBarTracking.editModalClosed({
      patient_id: patientId,
      data_type: 'diagnoses',
      had_changes: hasLocalChanges,
      source,
    });
    // If there are unsaved changes, flush immediately before closing
    if (hasLocalChanges && diagnoses.length >= 0) {
      await saveImmediately(diagnoses);
    }
    onClose();
  }, [hasLocalChanges, diagnoses, saveImmediately, onClose, patientId, source, contextBarTracking]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing during active save operation
        if (status === 'saving') {
          return;
        }
        handleClose();
      }}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: fullScreen ? '100%' : '80vh',
          maxHeight: fullScreen ? '100%' : '80vh',
          bgcolor: 'background.paper',
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {`Edit ${singularCap} Diagnoses`}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={status === 'saving'}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2 }}>
          <PatientDiagnosesEditCard
            diagnoses={diagnoses}
            onDiagnosesChange={handleDiagnosesChange}
            isLoading={isLoading}
            patientId={patientId}
            source={source}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <AutosaveStatus
          status={status}
          isDirty={hasLocalChanges}
          lastSavedAt={lastSavedAt}
          verbose
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={handleClose}
          variant="contained"
          disabled={status === 'saving'}
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          {status === 'saving' ? 'Saving...' : 'Done'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
